import type {
	ClassifierCategory,
	PiiScanTemplateType,
} from '@repo/api-codegen';

interface PolicyPiiScanBannerInformation {
	title: string;
	message: string;
	classifiers: ClassifierCategory[];
}

export const getPolicyPiiScanBannerInformation = (
	templateTitle: PiiScanTemplateType
): PolicyPiiScanBannerInformation => {
	switch (templateTitle) {
		case 'MISLABELED_PII_DETECTION':
			return {
				title: 'Mislabeled PII detection',
				message:
					"Filter specific databases, schemas, or tables by name. Then set metadata condition where 'PII is not true' to catch columns that aren't labeled as PII but contain sensitive data.",
				classifiers: ['PII.CONTACT', 'PII.ID_NUMBERS'],
			};
		case 'DATA_SPRAWL_DISCOVERY':
			return {
				title: 'Data sprawl discovery',
				message:
					"Filter for non-production databases (containing 'staging', 'backup', 'dev' in name). Then check where 'Description is not set' or 'Tags are not set' to find undocumented sensitive data.",
				classifiers: ['PII.CONTACT', 'PII.ID_NUMBERS'],
			};
		case 'PUBLIC_EXPOSURE_RISK':
			return {
				title: 'Public exposure risk',
				message:
					"Filter databases or tables that face external users. Then set metadata condition where 'Published is true' or check for tags indicating public/external access.",
				classifiers: ['PII.CONTACT', 'PII.ID_NUMBERS'],
			};
		case 'THIRD_PARTY_DATA_TRANSFER':
			return {
				title: 'Third-party data transfer',
				message:
					"Filter tables by name patterns (containing 'export', 'staging', 'etl'). Then check where 'Owner is set' to ensure transfer accountability and governance.",
				classifiers: ['PII.CONTACT', 'PII.ID_NUMBERS'],
			};
		case 'MISSING_DATA_STEWARDS':
			return {
				title: 'Missing data stewards',
				message:
					"Filter any resources you want to govern. Then set metadata condition where 'Owner is not set' to identify sensitive data lacking assigned stewardship.",
				classifiers: ['PII.CONTACT', 'PII.ID_NUMBERS'],
			};
		case 'DATA_DRIFT_MONITORING':
			return {
				title: 'Data drift monitoring',
				message:
					'Filter specific databases, schemas, or tables where data types may change. No metadata condition needed—recurring scans automatically detect when columns start containing PII.',
				classifiers: ['PII.CONTACT', 'PII.ID_NUMBERS'],
			};
		case 'RETENTION_POLICY_COMPLIANCE':
			return {
				title: 'Retention policy compliance',
				message:
					"Filter the databases or schemas subject to retention policies. Then set metadata condition where 'Updated At' is older than your retention period (e.g., before 2 years ago).",
				classifiers: ['PII.CONTACT'],
			};
		default:
			return {
				title: '',
				message: '',
				classifiers: [],
			};
	}
};
